import React, { useState, useEffect, useRef } from 'react';

// Helper to convert 12-hour time to 24-hour format for comparison
function convert12To24(time12) {
  if (!time12) return '';
  const match = time12.trim().match(/^(1[0-2]|0?[1-9]):([0-5][0-9]) ?([AaPp][Mm])$/);
  if (!match) return '';
  let hour = parseInt(match[1], 10);
  const minute = match[2];
  const ampm = match[3].toUpperCase();
  if (ampm === 'PM' && hour !== 12) hour += 12;
  if (ampm === 'AM' && hour === 12) hour = 0;
  return `${hour.toString().padStart(2, '0')}:${minute}`;
}

// Helper to check if a time is within available slots
function isTimeInSlots(time12, availableSlots) {
  if (!availableSlots || availableSlots.length === 0) return true;
  const time24 = convert12To24(time12);
  if (!time24) return false;

  return availableSlots.some(slot => {
    return time24 >= slot.from24 && time24 <= slot.to24;
  });
}

// Scrollable picker column component
function PickerColumn({ value, options, onChange, label }) {
  const columnRef = useRef(null);
  const itemHeight = 40; // Height of each item in pixels

  useEffect(() => {
    // Scroll to selected value
    if (columnRef.current && value) {
      const selectedIndex = options.findIndex(opt => opt.value === value);
      if (selectedIndex !== -1) {
        columnRef.current.scrollTop = selectedIndex * itemHeight - itemHeight * 2;
      }
    }
  }, [value, options]);

  return (
    <div className="flex flex-col items-center flex-1">
      <div className="text-sm font-bold text-gray-600 mb-2">{label}</div>
      <div
        ref={columnRef}
        className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
        style={{
          height: '280px',
          scrollbarWidth: 'thin',
        }}
      >
        <div style={{ height: '120px' }} /> {/* Top padding */}
        {options.map((opt) => (
          <div
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`cursor-pointer text-center transition-all duration-200 flex items-center justify-center ${
              value === opt.value
                ? 'text-white font-extrabold text-4xl'
                : 'text-gray-500 hover:bg-orange-50 text-xl'
            }`}
            style={{
              height: `${itemHeight}px`,
              borderRadius: value === opt.value ? '16px' : '0',
              margin: value === opt.value ? '0 8px' : '0',
              padding: value === opt.value ? '0 16px' : '0',
              background: value === opt.value ? 'linear-gradient(135deg, #FF7426 0%, #FF8C42 100%)' : 'transparent'
            }}
          >
            {opt.label}
          </div>
        ))}
        <div style={{ height: '120px' }} /> {/* Bottom padding */}
      </div>
    </div>
  );
}

export default function TimeSelect({ value, onChange, placeholder, disabled, isArabic, availableSlots, isToTime, fromTime }) {
  const [isOpen, setIsOpen] = useState(false);

  // Parse the value prop into separate parts
  const parseTime = (timeString) => {
    if (!timeString) return { hour: '1', minute: '00', period: 'AM' };
    const match = timeString.trim().match(/^(1[0-2]|0?[1-9]):([0-5][0-9]) ?([AaPp][Mm])$/);
    if (!match) return { hour: '1', minute: '00', period: 'AM' };
    return {
      hour: match[1],
      minute: match[2],
      period: match[3].toUpperCase()
    };
  };

  const [timeParts, setTimeParts] = useState(parseTime(value));

  // Update internal state when value prop changes
  useEffect(() => {
    if (value) {
      setTimeParts(parseTime(value));
    }
  }, [value]);

  // Handle changes to any time part
  const handlePartChange = (part, newValue) => {
    const newTimeParts = { ...timeParts, [part]: newValue };
    setTimeParts(newTimeParts);

    // Construct the full time string
    const fullTime = `${newTimeParts.hour}:${newTimeParts.minute} ${newTimeParts.period}`;

    // Validate against available slots and fromTime
    let isValid = isTimeInSlots(fullTime, availableSlots);

    if (isValid && isToTime && fromTime) {
      const from24 = convert12To24(fromTime);
      const current24 = convert12To24(fullTime);

      const containingSlot = availableSlots?.find(slot =>
        from24 >= slot.from24 && from24 <= slot.to24
      );

      if (containingSlot) {
        isValid = current24 > from24 && current24 <= containingSlot.to24;
      } else {
        isValid = false;
      }
    }

    if (isValid) {
      onChange(fullTime);
    }
  };

  const handleOk = () => {
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
    // Reset to original value
    if (value) {
      setTimeParts(parseTime(value));
    }
  };

  // Generate hour options (1-12)
  const hourOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1)
  }));

  // Minute options (00-59)
  const minuteOptions = [];
  for (let i = 0; i < 60; i++) {
    const min = i.toString().padStart(2, '0');
    minuteOptions.push({ value: min, label: min });
  }

  // Period options (AM, PM)
  const periodOptions = [
    { value: 'AM', label: 'AM' },
    { value: 'PM', label: 'PM' }
  ];

  // Display value
  const displayValue = value || (isArabic ? 'اختر الوقت' : 'Select time');

  return (
    <div className="relative w-full" style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif" }}>
      {/* Input trigger */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full h-12 rounded-xl border-2 border-orange-100 bg-orange-50 text-base font-semibold text-gray-900 shadow-sm transition-all duration-150 hover:border-orange-400 hover:bg-white focus:border-orange-500 focus:ring-4 focus:ring-orange-100 flex items-center justify-between px-4"
      >
        <span className="flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {displayValue}
        </span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Time picker modal */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[9998] flex items-center justify-center"
            onClick={handleCancel}
          />

          {/* Picker - Centered on screen */}
          <div
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-3xl shadow-2xl z-[9999]"
            style={{ width: '480px', maxWidth: '90vw' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="border-b border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <span className="text-base font-bold text-gray-600">
                  {isArabic ? 'الوقت' : 'Time'}
                </span>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="text-5xl font-bold mt-3" style={{
                background: 'linear-gradient(135deg, #FF7426 0%, #FF8C42 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {timeParts.hour}:{timeParts.minute} {timeParts.period}
              </div>
            </div>

            {/* Scrollable columns */}
            <div className="flex gap-4 p-6">
              <PickerColumn
                value={timeParts.hour}
                options={hourOptions}
                onChange={(val) => handlePartChange('hour', val)}
                label={isArabic ? 'ساعة' : 'Hour'}
              />
              <PickerColumn
                value={timeParts.minute}
                options={minuteOptions}
                onChange={(val) => handlePartChange('minute', val)}
                label={isArabic ? 'دقيقة' : 'Min'}
              />
              <PickerColumn
                value={timeParts.period}
                options={periodOptions}
                onChange={(val) => handlePartChange('period', val)}
                label=""
              />
            </div>

            {/* Footer buttons */}
            <div className="border-t border-gray-200 p-6 flex justify-end gap-4">
              <button
                onClick={handleCancel}
                className="px-8 py-3 text-base font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
              >
                {isArabic ? 'إلغاء' : 'CANCEL'}
              </button>
              <button
                onClick={handleOk}
                className="px-8 py-3 text-base font-bold text-white rounded-xl transition-all hover:shadow-lg"
                style={{ background: 'linear-gradient(135deg, #FF7426 0%, #FF8C42 100%)' }}
              >
                {isArabic ? 'موافق' : 'OK'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
